import {
  BOOK_READER_SET_LOADING,
  BOOK_READER_SET_ERROR,
  BOOK_READER_SET_BOOKS,
  BOOK_READER_SET_CURRENT_BOOK,
  BOOK_READER_UPDATE_BOOK,
  BOOK_READER_REMOVE_BOOK,
  BOOK_READER_CHAT_STREAM_CHUNK,
  BOOK_READER_CHAT_STREAM_DONE,
  BOOK_READER_CHAT_RESET,
  BOOK_READER_RESET,
  BOOK_READER_CHAT_ADD_USER_MSG,
  BOOK_READER_CHAT_FINALIZE_ASSISTANT,
  BOOK_READER_CHAT_VOICE_TRANSCRIPTION,
  BOOK_READER_SET_CURRENT_CHAT_ID,
  BOOK_READER_CHAT_LOAD_MESSAGES,
  BOOK_READER_CLEAR_CHAT_SESSION,
  BOOK_READER_SET_CHAT_LIST,
  BOOK_READER_CHAT_UPSERT_LIST_ITEM,
} from "./BookReaderTypes";

const initialState = {
  loading: false,
  error: null,
  books: [],
  currentBook: null,
  chatMessages: [],
  chatChunks: [],
  chatDone: false,
  chatDonePayload: null,
  currentBookReaderChatId: null,
  bookReaderChatList: [],
};

export const bookReaderReducer = (state = initialState, action) => {
  switch (action.type) {
    case BOOK_READER_SET_LOADING:
      return { ...state, loading: action.payload, ...(action.payload ? { error: null } : {}) };
    case BOOK_READER_SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case BOOK_READER_SET_BOOKS:
      return { ...state, books: action.payload ?? [] };
    case BOOK_READER_SET_CURRENT_BOOK:
      return { ...state, currentBook: action.payload ?? null };
    case BOOK_READER_UPDATE_BOOK: {
      const { _id, ...rest } = action.payload || {};
      if (!_id) return state;
      const books = state.books.map((b) => (b._id === _id ? { ...b, ...rest } : b));
      const currentBook =
        state.currentBook?._id === _id ? { ...state.currentBook, ...rest } : state.currentBook;
      return { ...state, books, currentBook };
    }
    case BOOK_READER_REMOVE_BOOK: {
      const idToRemove = action.payload;
      if (!idToRemove) return state;
      const books = state.books.filter(
        (b) => b._id !== idToRemove && b.bookMongoDBId !== idToRemove,
      );
      const currentBook =
        state.currentBook &&
        (state.currentBook._id === idToRemove || state.currentBook.bookMongoDBId === idToRemove)
          ? null
          : state.currentBook;
      return { ...state, books, currentBook };
    }
    case BOOK_READER_CHAT_STREAM_CHUNK:
      return {
        ...state,
        chatChunks: [...state.chatChunks, action.payload].filter(Boolean),
      };
    case BOOK_READER_CHAT_STREAM_DONE: {
      const p = action.payload || {};
      return {
        ...state,
        chatDone: true,
        chatDonePayload: p,
        ...(p.chatId ? { currentBookReaderChatId: p.chatId } : {}),
      };
    }
    case BOOK_READER_SET_CURRENT_CHAT_ID:
      return { ...state, currentBookReaderChatId: action.payload ?? null };
    case BOOK_READER_CHAT_LOAD_MESSAGES:
      return {
        ...state,
        chatMessages: Array.isArray(action.payload) ? action.payload : [],
        chatChunks: [],
        chatDone: false,
        chatDonePayload: null,
      };
    case BOOK_READER_CLEAR_CHAT_SESSION:
      return {
        ...state,
        chatMessages: [],
        chatChunks: [],
        chatDone: false,
        chatDonePayload: null,
        currentBookReaderChatId: null,
      };
    case BOOK_READER_SET_CHAT_LIST:
      return {
        ...state,
        bookReaderChatList: Array.isArray(action.payload) ? action.payload : [],
      };
    case BOOK_READER_CHAT_UPSERT_LIST_ITEM: {
      const item = action.payload;
      if (!item?._id) return state;
      const list = [...(state.bookReaderChatList || [])];
      const idx = list.findIndex((c) => c._id === item._id);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...item };
      } else {
        list.unshift(item);
      }
      list.sort((a, b) => (b.updateTime || 0) - (a.updateTime || 0));
      return { ...state, bookReaderChatList: list };
    }
    case BOOK_READER_CHAT_ADD_USER_MSG: {
      const p = action.payload;
      const userMsg =
        typeof p === "string"
          ? { role: "user", content: p }
          : {
              role: "user",
              content: p?.content ?? "",
              ...(p?.voiceUrl && { voiceUrl: p.voiceUrl }),
              ...(p?.voiceTranscription != null && {
                voiceTranscription: p.voiceTranscription,
              }),
            };
      return {
        ...state,
        chatMessages: [...state.chatMessages, userMsg],
      };
    }
    case BOOK_READER_CHAT_VOICE_TRANSCRIPTION: {
      const text = typeof action.payload === "string" ? action.payload : "";
      if (!text) return state;
      const next = [...state.chatMessages];
      for (let i = next.length - 1; i >= 0; i--) {
        if (
          next[i].role === "user" &&
          next[i].voiceUrl &&
          next[i].voiceTranscription == null
        ) {
          next[i] = { ...next[i], voiceTranscription: text };
          return { ...state, chatMessages: next };
        }
      }
      return state;
    }
    case BOOK_READER_CHAT_FINALIZE_ASSISTANT: {
      const assistantText = state.chatChunks.join("");
      const mixedResponse =
        action.payload?.mixedResponse &&
        typeof action.payload.mixedResponse === "object"
          ? action.payload.mixedResponse
          : null;
      const cleared = {
        ...state,
        chatChunks: [],
        chatDone: false,
        chatDonePayload: null,
      };
      if (!assistantText.trim() && !mixedResponse) return cleared;
      return {
        ...cleared,
        chatMessages: [
          ...state.chatMessages,
          {
            role: "assistant",
            content: assistantText,
            ...(mixedResponse && { rawPayload: mixedResponse }),
          },
        ],
      };
    }
    case BOOK_READER_CHAT_RESET:
      return { ...state, chatChunks: [], chatDone: false, chatDonePayload: null };
    case BOOK_READER_RESET:
      return { ...initialState };
    default:
      return state;
  }
};
